import React, { useEffect, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { countriesService, GET_CITIES, GET_STATES } from '@/api';
import { FormItem } from './FormItem';
import { GenericLocationI, Option } from '@/interfaces';
import { InputList, InputText } from '@/components/form';
import {
	FieldErrors,
	FieldValues,
	UseFormRegister,
	UseFormSetValue,
	UseFormWatch,
} from 'react-hook-form';
import { useNotification } from '@/hooks';
import { formatErrorMessage } from '@/lib';
import { CityData } from '@/interfaces/countries';
import { useDebounce } from 'use-debounce';
import clsx from 'clsx';

const normalizeLocationName = (value: string) =>
	value.trim().toLowerCase().replace(/\./g, '');

const getOptionLabelText = (label: Option['label']): string =>
	typeof label === 'string' ? label : '';

const findLocationMatch = (
	options: Option[] | undefined,
	googleName: string
): Option | undefined => {
	if (!options || !googleName) return undefined;
	const normalizedGoogle = normalizeLocationName(googleName);
	return options.find(
		(option) =>
			!!option.name &&
			!option.placeholder &&
			normalizeLocationName(getOptionLabelText(option.label)) ===
				normalizedGoogle
	);
};

interface Props {
	initialState: string;
	initialCity: GenericLocationI | undefined;
	rules?: any;
	className?: string;
	classNameInput?: string;
	errors?: FieldErrors<FieldValues>;
	register: UseFormRegister<FieldValues>;
	setValue: UseFormSetValue<FieldValues>;
	watch: UseFormWatch<FieldValues>;
	googleStateName?: string;
	googleCityName?: string;
	/** Called once autofill from Google has finished (match or no-match), so parent can clear autofill props */
	onGoogleAutofillComplete?: () => void;
	/** Show City/State as disabled text instead of dropdowns. Name matching still runs. */
	locked?: boolean;
}

export const FormSelectLocations: React.FC<Props> = ({
	initialState,
	initialCity,
	rules,
	errors,
	register,
	setValue,
	watch,
	className,
	classNameInput,
	googleStateName,
	googleCityName,
	onGoogleAutofillComplete,
	locked = false,
}) => {
	const observer = React.useRef<IntersectionObserver | null>(null);
	const [scrollTarget, setScrollTarget] = useState<string | null>(null);
	const scrollExecuted = React.useRef(false);

	const { toastError } = useNotification();
	const [allStates, setAllStates] = useState<Option[]>();
	const [defaultCityOption, setDefaultCityOption] = useState<Option | null>(
		null
	);

	const [stateSelected, setStateSelected] = useState<string>(
		initialState || ''
	);
	const [citySelected, setCitySelected] = useState<Option | undefined>();
	const [searchTermCity, setSearchTermCity] = React.useState<string>('');
	const [searchFilter] = useDebounce(searchTermCity, 500);
	const userChangedCity = React.useRef(false);

	// Fetch states
	const { data: states } = useQuery({
		queryKey: [GET_STATES],
		queryFn: () => countriesService.getStates('1'),
	});

	// Fetch default city by ID
	const { data: defaultCityData } = useQuery({
		queryKey: ['GET_CITY_BY_ID', initialCity],
		queryFn: () => countriesService.getCityById(String(initialCity?.id)),
		enabled: !!initialCity,
	});

	// Fetch paginated cities
	const {
		data: cities,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isLoading: isCitiesQueryLoading,
		refetch,
	} = useInfiniteQuery<CityData>({
		queryKey: [GET_CITIES, stateSelected, searchFilter],
		queryFn: async ({ pageParam = 1 }) => {
			const response = await countriesService.getCities(
				stateSelected,
				pageParam as number,
				searchFilter,
				100
			);
			return response.data;
		},
		getNextPageParam: (lastPage) => {
			return lastPage.page < lastPage.totalPages
				? lastPage.page + 1
				: undefined;
		},
		initialPageParam: 1,
		enabled: !!stateSelected,
	});

	const allCities = React.useMemo(() => {
		if (!cities?.pages)
			return [
				{
					name: '',
					label: 'Select your City',
					placeholder: true,
				},
			];

		const auxCities = cities.pages.flatMap((page) =>
			page.cities.map((city) => ({
				label: city.name,
				name: `${city.id}`,
				placeholder: false,
			}))
		);

		if (initialState !== stateSelected) {
			auxCities.unshift({
				name: '',
				label: 'Select your City',
				placeholder: true,
			});
		}

		return auxCities;
	}, [cities, initialState, stateSelected]);

	// Handle default city selection separately
	useEffect(() => {
		if (
			defaultCityData?.data?.city &&
			!userChangedCity.current &&
			stateSelected === initialState
		) {
			const city = defaultCityData.data.city;
			const option = {
				label: city.name,
				name: String(city.id),
				placeholder: false,
			};
			setDefaultCityOption(option);
			setCitySelected(option);
			setValue('city', city.id, { shouldValidate: true });
			setValue('cityName', city.name, { shouldValidate: true });
		}
	}, [defaultCityData, setValue, stateSelected, initialState]);

	// Set states options
	useEffect(() => {
		if (states?.data.states) {
			const auxStates: Option[] = [
				{ name: '', label: 'Select your State', placeholder: true },
			];

			states.data.states.forEach((state) =>
				auxStates.push({
					label: state.name,
					name: `${state.id}`,
				})
			);
			setAllStates(auxStates);
		}
	}, [states]);

	// Sync local state from props (hydration). Do not clear city here — that race
	// fires while stateSelected still lags initialState and blocks default-city restore.
	useEffect(() => {
		if (initialState) {
			setStateSelected(String(initialState));
		}
	}, [initialState]);

	const clearCityForStateChange = React.useCallback(() => {
		setValue('city', '');
		setValue('cityName', '');
		setCitySelected(undefined);
		setDefaultCityOption(null);
		userChangedCity.current = false;
	}, [setValue]);

	useEffect(() => {
		if (!scrollTarget || !allCities || scrollExecuted.current) return;

		const cityOption = allCities.find((c) => c.name === scrollTarget);
		if (!cityOption) return;

		const element = document.querySelector(`[data-city-id="${scrollTarget}"]`);
		if (element) {
			element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
			scrollExecuted.current = true;

			setTimeout(() => setScrollTarget(null), 1000);
			setValue('city', String(cityOption.name), { shouldValidate: true });
			setValue('cityName', cityOption.label, { shouldValidate: true });
			setDefaultCityOption(cityOption);
			setCitySelected(cityOption);
		}
	}, [allCities, scrollTarget, setValue]);

	const prevGoogleStateName = React.useRef<string | undefined>(undefined);
	const googleStateApplied = React.useRef(false);
	const prevGoogleCityName = React.useRef<string | undefined>(undefined);
	const googleCityApplied = React.useRef(false);
	const googleCitySearchKey = React.useRef<string | null>(null);
	const prevStateForGoogleCity = React.useRef<string>(stateSelected);

	const clearCitySelection = React.useCallback(() => {
		setCitySelected(undefined);
		setDefaultCityOption(null);
		setValue('city', '');
		setValue('cityName', '');
		setSearchTermCity('');
	}, [setValue]);

	const clearStateSelection = React.useCallback(() => {
		setStateSelected('');
		setValue('state', '');
		setValue('stateName', '');
		clearCitySelection();
	}, [setValue, clearCitySelection]);

	// Auto-fill state from google address (only when googleStateName changes)
	useEffect(() => {
		if (!googleStateName || !allStates) {
			if (!googleStateName) {
				prevGoogleStateName.current = undefined;
				googleStateApplied.current = false;
			}
			return;
		}

		if (googleStateName !== prevGoogleStateName.current) {
			googleStateApplied.current = false;
			prevGoogleStateName.current = googleStateName;
		}

		if (googleStateApplied.current) return;

		const matchingState = findLocationMatch(allStates, googleStateName);

		googleStateApplied.current = true;

		if (matchingState?.name) {
			const stateLabel = getOptionLabelText(matchingState.label);
			setStateSelected(matchingState.name);
			setValue('state', matchingState.name, { shouldValidate: true });
			setValue('stateName', stateLabel, { shouldValidate: true });
			// City autofill continues in the other effect; if no city was provided, we're done
			if (!googleCityName) {
				onGoogleAutofillComplete?.();
			}
			return;
		}

		// No match in our states list → leave blank and reset searchers
		clearStateSelection();
		onGoogleAutofillComplete?.();
	}, [
		googleStateName,
		googleCityName,
		allStates,
		setValue,
		clearStateSelection,
		onGoogleAutofillComplete,
	]);

	// Auto-fill city from google address (only when googleCityName changes / state settles)
	useEffect(() => {
		if (!googleCityName || !stateSelected) {
			if (!googleCityName) {
				prevGoogleCityName.current = undefined;
				googleCityApplied.current = false;
				googleCitySearchKey.current = null;
			}
			return;
		}

		// New google city OR state changed after google pick → retry match for this state
		const googleCityChanged = googleCityName !== prevGoogleCityName.current;
		const stateChangedForCity =
			prevStateForGoogleCity.current !== stateSelected;

		if (googleCityChanged) {
			googleCityApplied.current = false;
			prevGoogleCityName.current = googleCityName;
			googleCitySearchKey.current = null;
		}

		if (stateChangedForCity) {
			prevStateForGoogleCity.current = stateSelected;
			if (googleCityName) {
				googleCityApplied.current = false;
				googleCitySearchKey.current = null;
			}
		}

		if (googleCityApplied.current) return;
		if (isCitiesQueryLoading || isFetchingNextPage) return;

		const matchingCity = findLocationMatch(allCities, googleCityName);

		if (matchingCity?.name) {
			const cityLabel = getOptionLabelText(matchingCity.label);
			googleCityApplied.current = true;
			googleCitySearchKey.current = null;
			setCitySelected(matchingCity);
			setValue('city', matchingCity.name, { shouldValidate: true });
			setValue('cityName', cityLabel, { shouldValidate: true });
			setSearchTermCity('');
			onGoogleAutofillComplete?.();
			return;
		}

		const searchKey = normalizeLocationName(googleCityName);
		const currentFilter = normalizeLocationName(searchFilter);

		// Not in current results — kick off a single search and wait for it
		if (currentFilter !== searchKey) {
			if (googleCitySearchKey.current !== searchKey) {
				googleCitySearchKey.current = searchKey;
				setSearchTermCity(googleCityName.trim());
			}
			return;
		}

		// Search completed with no exact match → leave blank and reset searcher
		googleCityApplied.current = true;
		clearCitySelection();
		onGoogleAutofillComplete?.();
	}, [
		googleCityName,
		stateSelected,
		allCities,
		searchFilter,
		isCitiesQueryLoading,
		isFetchingNextPage,
		setValue,
		clearCitySelection,
		onGoogleAutofillComplete,
	]);

	const handleDeleteCity = async (cityId: string) => {
		try {
			const city = cities?.pages
				.flatMap((page) => page.cities)
				.find((city) => city.id === Number(cityId));

			if (city) {
				await countriesService.deleteCity(Number(city.id));
				if (initialCity) {
					setScrollTarget(String(initialCity.id));
					const search =
						initialCity.name.length > 3
							? initialCity.name.slice(0, 3)
							: initialCity.name;
					setSearchTermCity(search);
					scrollExecuted.current = false;
				} else {
					refetch();
				}
			}
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		}
	};

	const handleCity = async (
		cityName: string,
		callback: any,
		cityId: string | number | undefined
	) => {
		try {
			const stateId = watch('state');
			if (!stateId) return;

			userChangedCity.current = true;
			let id = cityId;
			if (cityId) {
				const response = await countriesService.updateCity(Number(cityId), {
					name: cityName,
					stateId: Number(stateId),
				});
				id = response.data.id;
				setScrollTarget(String(id));
			} else {
				const response = await countriesService.createCity({
					name: cityName,
					stateId: Number(stateId),
				});
				id = response.data.id;
				setScrollTarget(String(id));
			}

			const initials = cityName.slice(0, 2);
			const newOption: Option = {
				label: cityName,
				name: String(id),
				placeholder: false,
			};
			setCitySelected(newOption);
			setDefaultCityOption(newOption);
			setValue('city', String(id), { shouldValidate: true, shouldDirty: true });
			setValue('cityName', cityName, {
				shouldValidate: true,
				shouldDirty: true,
			});
			setSearchTermCity(initials.toLocaleLowerCase());
			scrollExecuted.current = false;
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		} finally {
			callback();
		}
	};

	const lastItemCallback = React.useCallback(
		(node: HTMLDivElement | HTMLLIElement | null) => {
			if (isFetchingNextPage) return;

			if (observer.current) observer.current.disconnect();
			observer.current = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting && hasNextPage) {
					fetchNextPage();
				}
			});

			if (node) observer.current.observe(node);
		},
		[isFetchingNextPage, hasNextPage, fetchNextPage]
	);

	const handleCitySelectionChange = (selectedCityId: string) => {
		// Placeholder / empty resets are not user picks — must not block default-city hydrate
		if (!selectedCityId) {
			setCitySelected(undefined);
			return;
		}

		userChangedCity.current = true;
		let newSelectedOption: Option | undefined | null = null;

		if (allCities) {
			newSelectedOption = allCities.find((opt) => opt.name === selectedCityId);
		}

		if (newSelectedOption) {
			const cityLabel = getOptionLabelText(newSelectedOption.label);
			setCitySelected(newSelectedOption);
			if (watch('cityName') !== cityLabel) {
				setValue('cityName', cityLabel, { shouldValidate: true });
			}
		} else {
			console.warn(
				`Selected city ID ${selectedCityId} not found in current options.`
			);
		}
	};
	const lockedInputClass = clsx('w-full md:w-[500px]', classNameInput);

	if (locked) {
		return (
			<>
				<FormItem label="State" className={className}>
					<input type="hidden" {...register('state')} />
					<InputText
						name="stateName"
						register={register}
						disabled
						placeholder="Select your State"
						className={lockedInputClass}
						classNameInput="!mt-0 disabled:!bg-[#F4F4F4A3] disabled:!text-black group-hover:disabled:!bg-[#F4F4F4A3]"
						rules={rules?.state}
						error={errors?.stateName}
					/>
				</FormItem>
				<FormItem label="City" className={className}>
					<input type="hidden" {...register('city')} />
					<InputText
						name="cityName"
						register={register}
						disabled
						placeholder="Select your City"
						className={lockedInputClass}
						classNameInput="!mt-0 disabled:!bg-[#F4F4F4A3] disabled:!text-black group-hover:disabled:!bg-[#F4F4F4A3]"
						rules={rules?.city}
						error={errors?.cityName}
					/>
				</FormItem>
			</>
		);
	}

	return (
		<>
			<FormItem label="State" className={className}>
				{allStates && (
					<InputList
						myDefaultValue={stateSelected}
						register={register}
						name="state"
						options={allStates}
						className={clsx('w-full md:w-[500px]', classNameInput)}
						setValueInput={setValue}
						handleChange={(value) => {
							// InputList can emit an empty placeholder callback on mount/sync.
							// Ignore it when we already have (or are hydrating) a real state.
							if (
								!value &&
								(initialState || stateSelected) &&
								value !== stateSelected
							) {
								return;
							}
							if (value !== stateSelected) {
								clearCityForStateChange();
							}
							setStateSelected(value);
							const stateOption = allStates.find((s) => s.name === value);
							const stateLabel = stateOption
								? getOptionLabelText(stateOption.label)
								: '';
							if (stateOption && watch('stateName') !== stateLabel) {
								setValue('stateName', stateLabel, {
									shouldValidate: true,
								});
							}
						}}
						classNameInput="!mt-0"
						rules={rules?.state}
						error={errors?.state}
					/>
				)}
			</FormItem>

			<FormItem label="City" className={className}>
				{(allCities || defaultCityOption) && (
					<InputList
						from="city"
						myDefaultValue={citySelected?.name}
						myDefaultValueOption={citySelected}
						name="city"
						register={register}
						options={allCities || []}
						setValueInput={setValue}
						className={clsx('w-full md:w-[500px]', classNameInput)}
						disabled={!stateSelected}
						loading={isCitiesQueryLoading}
						resetValue={!watch('city')}
						maxHeight="bottom-full mb-1"
						isSearch
						showActionButtons
						setInputCreate={handleCity}
						setInputDelete={handleDeleteCity}
						containerRef={lastItemCallback}
						handleSearch={(value) => setSearchTermCity(value)}
						handleChange={handleCitySelectionChange}
						rules={rules?.city}
						error={errors?.city}
						classNameInput="!mt-0"
					/>
				)}
			</FormItem>
		</>
	);
};
